class CustomCarousel extends window.HTMLElement {
    constructor() {
        super();
        this.selectors = {
            loop: "data-loop",
            scrollCount: "[data-scroll-num]",
            dragFree: "data-drag-free",
            align: "data-align",
            controls: "data-controls",
            autoplay: "data-autoplay",
            autoplayInterval: "data-autoplay-interval",
            carouselContainer: ".carousel-container",
            progressBar: ".progress-bar"
        };

        this.autoplayPlugin = null;
        this.observer = null;
        this.autoplayInterval = null;
    }

    connectedCallback() {
        const loop = this.hasAttribute(this.selectors.loop);
        const scrollCount = this.getAttribute(this.selectors.scrollCount) ?? 1;
        const dragFree = this.hasAttribute(this.selectors.dragFree);
        const controls = this.hasAttribute(this.selectors.controls);
        const align = this.getAttribute(this.selectors.align) ?? 'start';
        const autoplay = this.hasAttribute(this.selectors.autoplay);
        const autoplayInterval = parseInt(this.getAttribute(this.selectors.autoplayInterval)) || 5000;

        const carouselContainer = this.querySelector(this.selectors.carouselContainer);

        const plugins = [];

        if (autoplay) {
            this.autoplayPlugin = EmblaCarouselAutoplay({ delay: autoplayInterval });
            plugins.push(this.autoplayPlugin);
        }

        this.embla = EmblaCarousel(carouselContainer, {
            loop: loop,
            align: align,
            dragFree: dragFree,
            skipSnaps: true,
            slidesToScroll: parseInt(scrollCount),
        }, plugins);

        if (controls) {
            this.setupControls();
        }

        if (autoplay) {
            this.setupIntersectionObserver();
            this.resetProgressBar();
            this.startProgressBar();
        }

        this.embla.on('select', () => {
            this.resetProgressBar();
            this.startProgressBar();
            this.resetAutoplay();
        });
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        if (this.embla) {
            this.embla.destroy();
            this.embla = null;
        }
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    setupControls() {
        const prevBtnNodes = Array.from(this.querySelectorAll(".embla__button--prev"));
        const nextBtnNodes = Array.from(this.querySelectorAll(".embla__button--next"));

        prevBtnNodes.forEach((prevBtnNode, index) => {
            const nextBtnNode = nextBtnNodes[index];
            if (nextBtnNode) {
                const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
                    this.embla,
                    prevBtnNode,
                    nextBtnNode
                );
                this.embla.on('destroy', removePrevNextBtnsClickHandlers);
            }
        });
    }

    setupIntersectionObserver() {
        const options = {
            root: null,
            threshold: 0.1
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.autoplayPlugin.play();
                    this.startProgressBar();
                } else {
                    this.autoplayPlugin.stop();
                    this.resetProgressBar();
                }
            });
        }, options);

        this.observer.observe(this);
    }

    resetAutoplay() {
        if (this.autoplayPlugin) {
            this.autoplayPlugin.stop();
            this.autoplayPlugin.play();
        }
    }

    startProgressBar() {
        const progressBars = this.querySelectorAll(this.selectors.progressBar);
        if (progressBars.length === 0) return; // Check if progress bars exist

        const autoplayInterval = parseInt(this.getAttribute(this.selectors.autoplayInterval)) || 5000;
        progressBars.forEach(progressBar => {
            requestAnimationFrame(() => {
                progressBar.style.transition = `width ${autoplayInterval}ms linear`;
                progressBar.style.width = '100%';
            });
        });
    }

    resetProgressBar() {
        const progressBars = this.querySelectorAll(this.selectors.progressBar);
        if (progressBars.length === 0) return; // Check if progress bars exist

        progressBars.forEach(progressBar => {
            progressBar.style.transition = 'none';
            progressBar.style.width = '0%';
        });
    }

    addTogglePrevNextBtnsActive(emblaApi, prevBtn, nextBtn) {
        const togglePrevNextBtnsState = () => {
            if (emblaApi.canScrollPrev()) prevBtn.removeAttribute('disabled');
            else prevBtn.setAttribute('disabled', 'disabled');

            if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled');
            else nextBtn.setAttribute('disabled', 'disabled');
        };

        emblaApi
            .on('select', togglePrevNextBtnsState)
            .on('init', togglePrevNextBtnsState)
            .on('reInit', togglePrevNextBtnsState);

        return () => {
            prevBtn.removeAttribute('disabled');
            nextBtn.removeAttribute('disabled');
        };
    }

    addPrevNextBtnsClickHandlers(emblaApi, prevBtn, nextBtn) {
        const scrollPrev = () => {
            emblaApi.scrollPrev();
        };
        const scrollNext = () => {
            emblaApi.scrollNext();
        };
        prevBtn.addEventListener('click', scrollPrev, false);
        nextBtn.addEventListener('click', scrollNext, false);

        const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
            emblaApi,
            prevBtn,
            nextBtn
        );

        return () => {
            removeTogglePrevNextBtnsActive();
            prevBtn.removeEventListener('click', scrollPrev, false);
            nextBtn.removeEventListener('click', scrollNext, false);
        };
    }
}

window.customElements.define("custom-carousel", CustomCarousel);